/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module EmailValidationModule
 */

import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';
import { SysTestEmailParams } from 'object-types';
import { ClrFormLayout } from '@clr/angular';
import { ISysTestEmailParams } from 'generated-types';
import { Subscription } from 'rxjs';

import * as globalL10n from 'global-l10n';
import * as l10n from './email-validation-modal.l10n';

import { EmailValidationService } from './email-validation-modal.service';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

export const enum EmailModalTypeEnum {
    SYSLOG = 'syslog',
    SMTP = 'smtp',
    EMAIL = 'email',
    SNMP = 'snmp'
}

/**
 * @description Modal to validate emails.
 * @author Manideep Kotha
 */
@Component({
    selector: 'email-validation-modal',
    templateUrl: './email-validation-modal.component.html',
})
export class EmailValidationModalComponent implements OnInit, OnDestroy {
    /**
     * Specifies the type of email modal being displayed.
     */
    @Input()
    public emailModalType: EmailModalTypeEnum;

    /**
     * Used for API request.
     */
    @Input()
    public uuid: string;

    @Input()
    public config: ISysTestEmailParams = {};

    @Output()
    public readonly onCancel = new EventEmitter<void>();

    public errors?: string;

    public busy = false;

    public readonly globalL10nKeys = globalL10nKeys;

    public readonly l10nKeys = l10nKeys;

    public readonly objectType = SysTestEmailParams;

    public readonly verticalLayout = ClrFormLayout.VERTICAL;

    /**
     * The title to be displayed in the modal.
     * Its content may vary based on the modal type.
     */
    public modalTitle: string;

    /**
     * The label text to be displayed, varies by modal type.
     * Its content may vary depending on the modal type.
     */
    public textLabel: string;

    /**
     * A flag indicating whether to display text only in the modal,
     * Its behavior may vary depending on the modal type.
     */
    public showTextOnly = false;

    /**
     * A flag indicating whether to disable the ability to edit recipients,
     * Its behavior may vary depending on the modal type.
     */
    public disableEditRecipients = false;

    private subscription: Subscription;

    constructor(
        private readonly l10nService: L10nService,
        private readonly componentService: EmailValidationService,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnInit(): void {
        const {
            emailModalType,
            l10nService,
        } = this;

        /**
         * Configure the modal settings depending on the type of validation being done.
         * Modal can be opened from Administration -> System Settings or
         * Operations -> Notifications.
         */
        switch (emailModalType) {
            case EmailModalTypeEnum.SMTP:
                this.modalTitle = l10nService.getMessage(this.l10nKeys.smtpTitleLabel);
                this.textLabel = l10nService.getMessage(this.l10nKeys.bodyLabel);
                this.showTextOnly = false;
                this.disableEditRecipients = false;
                break;

            case EmailModalTypeEnum.EMAIL:
                this.modalTitle = l10nService.getMessage(this.l10nKeys.emailTitleLabel);
                this.textLabel = l10nService.getMessage(this.l10nKeys.bodyLabel);
                this.showTextOnly = false;
                this.disableEditRecipients = true;
                break;

            case EmailModalTypeEnum.SNMP:
                this.modalTitle = l10nService.getMessage(this.l10nKeys.snmpTitleLabel);
                this.textLabel = l10nService.getMessage(this.l10nKeys.snmpTextLabel);
                this.showTextOnly = true;
                this.disableEditRecipients = true;
                break;

            case EmailModalTypeEnum.SYSLOG:
                this.modalTitle = l10nService.getMessage(this.l10nKeys.sysLogTitleLabel);
                this.textLabel = l10nService.getMessage(globalL10nKeys.messageLabel);
                this.showTextOnly = true;
                this.disableEditRecipients = true;
                break;
        }
    }

    public ngOnDestroy(): void {
        if (this.subscription && !this.subscription.closed) {
            this.subscription.unsubscribe();
        }
    }

    public handleCancel(): void {
        this.onCancel.emit();
    }

    public handleSubmit(): void {
        this.busy = true;
        this.errors = '';

        /**
         * Make POST request to validate email settings.
         */
        this.subscription = this.componentService.fetchData(
            this.uuid,
            this.config,
            this.emailModalType,
        ).subscribe({
            next: () => this.onCancel.emit(),
            error: errors => this.errors = errors,
        });

        this.subscription.add(() => {
            this.busy = false;
            this.subscription.unsubscribe();
        });
    }
}
